import { useState } from "react";
import { PaymentMethodLabel, type CashClose } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { dayBounds, fmtDateTime, money, parseMoney, todayIso } from "../lib/format.js";
import { useBranch, useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { CashClosePrintActions } from "../components/PrintActions.js";
import { Alert, Button, Card, Empty, Field, Input, Loading, Money, PageHead, Select, Textarea } from "../components/ui.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";
import { CashMovementsCard, NetByMethod } from "../components/CashMovements.js";

/** Corte de caja: one user's drawer for a period, by method, with the counted cash and the variance. */
export function CashClosePage() {
  const branch = useBranch();
  const { me, can } = useSession();
  const toast = useToast();
  const [day, setDay] = useState(todayIso());
  const [userId, setUserId] = useState(me?.user.id ?? "");
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const { start, end } = dayBounds(day);
  const users = useQuery(() => get("users"), [], { enabled: can("administrador") });
  const summary = useQuery(() => get("cashCloseSummary", { query: { branch_id: branch.id, user_id: userId || undefined, period_start: start, period_end: end } }), [branch.id, userId, start, end]);
  const movements = useQuery(() => get("cashMovements", { query: { branch_id: branch.id, user_id: userId || undefined, period_start: start, period_end: end } }), [branch.id, userId, start, end]);
  const history = useQuery(() => get("cashCloses", { query: { branch_id: branch.id, limit: 30 } }), [branch.id]);

  const s = summary.data;
  const countedCents = parseMoney(counted);
  const variance = s && countedCents !== null ? countedCents - s.expected_cash_cents : null;
  const canClose = !!s && countedCents !== null && (s.unclosed_payment_ids.length > 0 || s.unclosed_movement_ids.length > 0) && (me?.user.can_close_cash || can("administrador"));

  async function close() {
    if (!s || countedCents === null) return;
    setBusy(true);
    try {
      const c = await run<"cash_close.create", CashClose>("cash_close.create", { branch_id: branch.id, user_id: userId || null, period_start: start, period_end: end, counted_cash_cents: countedCents, notes: notes.trim() || null }, { branch_id: branch.id });
      toast.success(`Corte registrado. ${c.variance_cents === 0 ? "Sin diferencia." : `Diferencia: ${money(c.variance_cents)}.`}`);
      setCounted("");
      setNotes("");
      void summary.reload();
      void movements.reload();
      void history.reload();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // A movement is recorded now, in the acting user's own drawer: only that drawer, today, can take one.
  const ownDrawer = !userId || userId === me?.user.id;
  const canAddMovement = ownDrawer && day === todayIso();
  const reloadMovements = () => {
    void summary.reload();
    void movements.reload();
  };

  return (
    <>
      <PageHead kicker={branch.name} title="Corte de caja" lede="Lo cobrado por cada persona en el día, por forma de pago, más los ingresos y egresos extra. El crédito se muestra aparte: no es dinero." />
      <div className="toolbar">
        <Field label="Día">
          <input {...NO_AUTOFILL} type="date" className="input" value={day} onChange={(e) => setDay(e.target.value)} />
        </Field>
        {can("administrador") && users.data && (
          <Field label="Cajero">
            <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
              {users.data.items.filter((u) => u.is_active).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>
      <div className="split">
        <div className="stack-lg">
          <Card
            title="Cobros del periodo"
            pad={false}
            footer={<CashClosePrintActions target={{ kind: "summary", query: { branch_id: branch.id, user_id: userId || undefined, period_start: start, period_end: end } }} disabled={!s} />}
          >
            {summary.loading && !s ? (
              <Loading />
            ) : !s ? (
              <Empty icon="cash" title="Sin datos" hint={summary.error ?? undefined} />
            ) : (
              <>
                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Forma de pago</th>
                        <th className="right">Pagos</th>
                        <th className="right">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.by_method.map((m) => (
                        <tr key={m.method}>
                          <td style={{ fontWeight: 500 }}>{PaymentMethodLabel[m.method]}</td>
                          <td className="right tnum">{m.count}</td>
                          <td className="right">
                            <Money cents={m.amount_cents} />
                          </td>
                        </tr>
                      ))}
                      {s.by_method.length === 0 && (
                        <tr>
                          <td colSpan={3} className="faint">
                            Sin cobros en el periodo.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ fontWeight: 600 }}>Total cobrado</td>
                        <td className="right tnum">{s.payments_count}</td>
                        <td className="right" style={{ fontWeight: 600 }}>
                          <Money cents={s.total_cents} />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="grid-3" style={{ padding: 18 }}>
                  <div className="stat">
                    <span className="label">Efectivo esperado</span>
                    <span className="value">{money(s.expected_cash_cents)}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Solicitudes</span>
                    <span className="value">{s.orders_count}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Crédito otorgado</span>
                    <span className="value muted">{money(s.credit_granted_cents)}</span>
                  </div>
                </div>
                <NetByMethod s={s} />
              </>
            )}
          </Card>

          <CashMovementsCard
            movements={movements.data?.items ?? []}
            canAdd={canAddMovement}
            addHint={!ownDrawer ? "Los movimientos se registran en tu propia caja." : "Los movimientos nuevos se registran en el día de hoy."}
            onChanged={reloadMovements}
          />

          <Card title="Cortes anteriores" pad={false}>
            {!history.data?.items.length ? (
              <div className="list-row faint">Todavía no hay cortes registrados en esta sucursal.</div>
            ) : (
              <div className="table-scroll">
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>Periodo</th>
                      <th>Cajero</th>
                      <th className="right">Cobrado</th>
                      <th className="right">Efectivo esperado</th>
                      <th className="right">Contado</th>
                      <th className="right">Diferencia</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {history.data.items.map((c) => (
                      <tr key={c.id}>
                        <td className="small nowrap">{fmtDateTime(c.period_start)} – {fmtDateTime(c.period_end)}</td>
                        <td>{c.summary.user_name}</td>
                        <td className="right">
                          <Money cents={c.summary.total_cents} />
                        </td>
                        <td className="right">
                          <Money cents={c.expected_cash_cents} />
                        </td>
                        <td className="right">
                          <Money cents={c.counted_cash_cents} />
                        </td>
                        <td className="right" style={{ color: c.variance_cents === 0 ? "var(--brand)" : "var(--red)", fontWeight: 600 }}>
                          {c.variance_cents === 0 ? "—" : money(c.variance_cents)}
                        </td>
                        <td className="right">
                          <CashClosePrintActions compact target={{ kind: "close", id: c.id }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
        <div className="sticky">
          <Card title="Cerrar caja">
            <div className="stack-lg">
              {s && s.unclosed_payment_ids.length === 0 && s.unclosed_movement_ids.length === 0 && <Alert tone="green">Todo lo de este periodo ya está en un corte.</Alert>}
              <Field label="Efectivo contado" hint={s ? `Debería haber ${money(s.expected_cash_cents)} en efectivo${s.movements_count ? " (con los movimientos)" : ""}.` : undefined}>
                <div className="input-wrap">
                  <span className="suffix">MXN</span>
                  <Input inputMode="decimal" value={counted} onChange={(e) => setCounted(e.target.value)} placeholder="0.00" className="lg" />
                </div>
              </Field>
              {variance !== null && (
                <Alert tone={variance === 0 ? "green" : "amber"}>
                  {variance === 0 ? "Cuadra exacto." : variance > 0 ? `Sobran ${money(variance)}.` : `Faltan ${money(-variance)}.`}
                </Alert>
              )}
              <Field label="Notas" optional>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
              </Field>
              <Button variant="primary" size="lg" block icon="check" busy={busy} disabled={!canClose} onClick={() => void close()}>
                Registrar corte
              </Button>
              {!me?.user.can_close_cash && !can("administrador") && <p className="small muted">Tu usuario no tiene permiso para cerrar caja.</p>}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
